import { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Upload, FileText, ExternalLink, Trash2, FileCheck, FileArchive, Loader2, X } from "lucide-react";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";

interface VehicleDocument {
  id: string;
  vehicle_id: string;
  name: string;
  document_type: string;
  file_url: string;
  file_name: string;
  file_size: number | null;
  created_at: string;
}

const DOC_TYPES = [
  { value: "carfax", label: "Carfax Report", color: "bg-blue-100 text-blue-700" },
  { value: "safety", label: "Safety Certificate", color: "bg-green-100 text-green-700" },
  { value: "bill_of_sale", label: "Bill of Sale", color: "bg-purple-100 text-purple-700" },
  { value: "inspection", label: "Inspection Report", color: "bg-orange-100 text-orange-700" },
  { value: "warranty", label: "Warranty Document", color: "bg-yellow-100 text-yellow-700" },
  { value: "other", label: "Other", color: "bg-gray-100 text-gray-700" },
];

function getDocTypeConfig(type: string) {
  return DOC_TYPES.find(d => d.value === type) || DOC_TYPES[DOC_TYPES.length - 1];
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface VehicleDocumentsProps {
  vehicleId: string;
}

export function VehicleDocuments({ vehicleId }: VehicleDocumentsProps) {
  const [documents, setDocuments] = useState<VehicleDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [docName, setDocName] = useState("");
  const [docType, setDocType] = useState("other");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deleteTarget, setDeleteTarget] = useState<VehicleDocument | null>(null);

  useEffect(() => {
    loadDocuments();
  }, [vehicleId]);

  const loadDocuments = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("vehicle_documents")
      .select("*")
      .eq("vehicle_id", vehicleId)
      .order("created_at", { ascending: false });

    if (!error) setDocuments((data as VehicleDocument[]) || []);
    setIsLoading(false);
  };

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    if (!docName) {
      // Auto-set name from filename (strip extension)
      setDocName(file.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " "));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !docName.trim()) {
      toast.error("Please select a file and enter a document name");
      return;
    }

    // Max 25MB
    if (selectedFile.size > 25 * 1024 * 1024) {
      toast.error("File must be under 25MB");
      return;
    }

    // Allowed file types
    const allowedExts = ["pdf", "jpg", "jpeg", "png", "webp", "doc", "docx", "xls", "xlsx"];
    const ext = selectedFile.name.split(".").pop()?.toLowerCase() || "";
    if (!allowedExts.includes(ext)) {
      toast.error("File type not allowed. Use PDF, images, or Office documents.");
      return;
    }

    setIsUploading(true);
    try {
      // Upload to Supabase Storage
      const ext = selectedFile.name.split(".").pop();
      const filePath = `${vehicleId}/${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("vehicle-documents")
        .upload(filePath, selectedFile);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("vehicle-documents")
        .getPublicUrl(filePath);

      // Save record to DB
      const { error: dbError } = await supabase.from("vehicle_documents").insert({
        vehicle_id: vehicleId,
        name: docName.trim(),
        document_type: docType,
        file_url: filePath, // Store the path instead of public URL
        file_name: selectedFile.name,
        file_size: selectedFile.size,
      });

      if (dbError) throw dbError;

      toast.success("Document uploaded successfully");
      setDocName("");
      setDocType("other");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      loadDocuments();
    } catch (err: any) {
      toast.error("Upload failed: " + (err.message || "Unknown error"));
    } finally {
      setIsUploading(false);
    }
  };

  const deleteDocument = async (doc: VehicleDocument) => {
    // Delete from storage
    let storagePath = doc.file_url;
    if (storagePath.includes("/storage/v1/object/public/vehicle-documents/")) {
      storagePath = storagePath.split("/vehicle-documents/")[1];
    }
    
    if (storagePath) {
      await supabase.storage.from("vehicle-documents").remove([storagePath]);
    }

    const { error } = await supabase.from("vehicle_documents").delete().eq("id", doc.id);
    if (error) {
      toast.error("Failed to delete document");
    } else {
      toast.success("Document deleted");
      loadDocuments();
    }
    setDeleteTarget(null);
  };

  const handleView = async (doc: VehicleDocument) => {
    // If the URL is already a full URL (legacy), try to extract path
    let path = doc.file_url;
    if (path.includes("/storage/v1/object/public/vehicle-documents/")) {
      path = path.split("/vehicle-documents/")[1];
    }

    const { data, error } = await supabase.storage
      .from("vehicle-documents")
      .createSignedUrl(path, 3600); // 1 hour

    if (error || !data) {
      toast.error("Failed to generate document link");
      return;
    }

    window.open(data.signedUrl, "_blank");
  };

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div className="border rounded-xl p-5 bg-card">
        <h3 className="font-semibold text-base mb-4 flex items-center gap-2">
          <Upload className="h-4 w-4 text-primary" /> Upload Document
        </h3>

        {/* Drop Zone */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer mb-4 ${
            isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/30 hover:border-primary/50"
          }`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          {selectedFile ? (
            <div className="flex items-center justify-center gap-3">
              <FileCheck className="h-8 w-8 text-green-500" />
              <div className="text-left">
                <p className="font-medium text-sm">{selectedFile.name}</p>
                <p className="text-xs text-muted-foreground">{formatFileSize(selectedFile.size)}</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 ml-2 text-muted-foreground hover:text-red-500"
                onClick={(e) => { e.stopPropagation(); setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <>
              <FileArchive className="h-10 w-10 text-muted-foreground/50 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Drag & drop a file here, or <span className="text-primary underline">browse</span>
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">PDF, PNG, JPG, DOCX supported</p>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.png,.jpg,.jpeg,.docx,.doc,.xlsx"
            onChange={(e) => { if (e.target.files?.[0]) handleFileSelect(e.target.files[0]); }}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs mb-1 block">Document Name *</Label>
            <Input
              placeholder="e.g. Carfax Report, Safety Certificate"
              value={docName}
              onChange={(e) => setDocName(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Document Type</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <Button
            onClick={handleUpload}
            disabled={isUploading || !selectedFile || !docName.trim()}
            className="min-w-[120px]"
          >
            {isUploading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
            ) : (
              <><Upload className="h-4 w-4 mr-2" /> Upload</>
            )}
          </Button>
        </div>
      </div>

      {/* Document List */}
      <div>
        <h3 className="font-semibold text-base mb-3 flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" /> Uploaded Documents
          {documents.length > 0 && (
            <Badge variant="secondary" className="text-xs">{documents.length}</Badge>
          )}
        </h3>

        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground text-sm">Loading documents...</div>
        ) : documents.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-muted rounded-xl text-muted-foreground text-sm">
            <FileArchive className="h-10 w-10 mx-auto mb-2 opacity-30" />
            No documents uploaded yet
          </div>
        ) : (
          <div className="space-y-2">
            {documents.map((doc) => {
              const typeConfig = getDocTypeConfig(doc.document_type);
              return (
                <div
                  key={doc.id}
                  className="flex items-center gap-3 p-3 border rounded-lg bg-card hover:bg-muted/30 transition-colors group"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{doc.name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${typeConfig.color}`}>
                        {typeConfig.label}
                      </span>
                      {doc.file_size && (
                        <span className="text-xs text-muted-foreground">{formatFileSize(doc.file_size)}</span>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {new Date(doc.created_at).toLocaleDateString("en-CA")}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-primary"
                      onClick={() => handleView(doc)}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => setDeleteTarget(doc)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        onConfirm={() => deleteTarget && deleteDocument(deleteTarget)}
        title="Delete Document"
        description={deleteTarget ? `Delete "${deleteTarget.name}"? This action cannot be undone.` : ""}
        confirmLabel="Delete"
        variant="destructive"
      />
    </div>
  );
}
